import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import * as _ from 'lodash';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

import { AppConfig } from '../../../app.settings';
import { Preset } from '../models/preset.type';
import { WallCalibrationModel } from '../models/wall-calibration.type';
import { Wall } from '../models/wall.type';
import { WallMonitorCalibrationModel } from '../models/wall-monitor-calibration.type';
import { WallMonitor } from '../models/monitor.type';
import { AuthService } from '../../../core/services/auth/auth.service';

// Preset is always PresetModel (SpaceWall or UsbWall)
@Injectable()
export class SharedPresetService {

    private storedPresets: Preset[] = [];
    public storedPresetsSubject: BehaviorSubject<Preset[]> = new BehaviorSubject<Preset[]>([]);
    public selectedMachinePhysicalAddress: BehaviorSubject<string> = new BehaviorSubject<string>(null);
    public calibratedWallIds: number[] = [];
    public shouldChangeActiveWallTab: Subject<number> = new Subject();
    public shouldTurnOffCalibration: Subject<void> = new Subject();
    public isPresetApplied: boolean;
    public createdPresetToApply: Subject<Preset> = new Subject();
    private endpoint: string;

    constructor(
        public readonly httpClient: HttpClient,
        public readonly config: AppConfig,
        public readonly authService: AuthService
    ) {
        this.endpoint = `${this.config.getConfig('api')}walls/presets`;
    }

    convertToWallCalibrationModel(wall: Wall): WallCalibrationModel {
        const wallMonitors: WallMonitorCalibrationModel[] = wall.Monitors.map((monitor: WallMonitor) =>
            new WallMonitorCalibrationModel(monitor));
        return new WallCalibrationModel(wall.Width, wall.Height, wallMonitors, wall.IsCalibrated, wall.WallIndex);
    }

    createNewPreset(preset: Preset): Observable<Preset> {
        return this.httpClient.post<Preset>(this.endpoint, this.fillPresetModel(preset)).pipe(
            map((createdpreset: Preset) => {
              if (preset.isActive) {
                  createdpreset.isActive = true;
                  this.createdPresetToApply.next(createdpreset);
                }
                this.storedPresets.push(createdpreset);
                this.storedPresetsSubject.next(this.storedPresets);
                return createdpreset;
            })
        );
    }

    getByPhysicalAddress(physicalAddress: string): Observable<Preset[]> {
        return this.httpClient.get<Preset[]>(`${this.endpoint}/${physicalAddress}`).pipe(
            map((presets: Preset[]) => {
                this.storedPresets = presets;
                this.storedPresetsSubject.next(presets);
                return presets;
            })
        );
    }

    updatePresetTitle(title: string, presetId: string): Observable<void> {
        const params = new HttpParams()
            .set('title', title);

        return this.httpClient.put<void>(`${this.endpoint}/${presetId}`, null, { params });
    }

    runPreset(preset: Preset) {
        this.shouldTurnOffCalibration.next();
        this.notifyUserWithPreset(preset).subscribe();
    }

    applyPreset(physicalAddress: string, preset: Preset): Observable<void> {
        return this.httpClient.post<void>(`${this.endpoint}/apply/${physicalAddress}`, this.fillPresetModel(preset));
    }

    deletePresetById(id: string): Observable<void> {
        return this.httpClient.delete<void>(`${this.endpoint}/${id}`).pipe(
            map(() => {
                _.remove(this.storedPresets, preset => preset.Id === id);
                this.storedPresetsSubject.next(this.storedPresets);
            })
        );
    }

    getPresetById(id: string): Observable<Preset> {
        return this.httpClient.get<Preset>(`${this.endpoint}/single/${id}`);
    }

    startCreation(physicalAddress: string): Observable<void> {
        return this.httpClient.get<void>(`${this.endpoint}/start-creation/${physicalAddress}`);
    }

    refreshPresetCall(physicalAddress: string): Observable<void> {
        return this.httpClient.get<void>(`${this.endpoint}/refresh-preset/${physicalAddress}`);
    }

    notifyUserWithPreset(preset: Preset): Observable<void> {
        return this.httpClient.post<void>(`${this.endpoint}/notify-user-with-preset/${this.authService.userId}`, preset);
    }

    public recountWallDimensions(preset: Preset): Preset {
        preset.Walls.forEach(wall => {
          if (!wall.IsAligned) {
            return;
          }

          this.recountMonitorPositions(wall);

          wall.Height = Math.round(wall.ResolutionHeight * wall.Rows + wall.BeselCorrectionHeight * (wall.Rows - 1));
          wall.Width = Math.round(wall.ResolutionWidth * wall.Columns + wall.BeselCorrectionWidth * (wall.Columns - 1));
        });

        return preset;
    }

    public recountMonitorPositions(wall: Wall): void {
        wall.Monitors.forEach(x => {
          x.LeftPosition = x.Column * wall.ResolutionWidth + wall.BeselCorrectionWidth * x.Column + (x.IsRotated ? wall.ResolutionWidth : 0);
          x.TopPosition = x.Row * wall.ResolutionHeight + wall.BeselCorrectionHeight * x.Row + (x.IsRotated ? wall.ResolutionHeight : 0);
        });
    }

    // TODO
    public fillPresetModel(preset: Preset): Preset {
        const modifyPreset: Preset = _.cloneDeep(preset);
        const valuesToFillIn: string[] = ['ExtendedGroup', 'MirrorGroups', 'Walls'];
        valuesToFillIn.forEach((value: string) => this.fillGroups(modifyPreset, value));

        return modifyPreset;
    }


    private fillGroups(preset: Preset, groupName: string): void {
        if (preset[groupName].length) {
            preset[groupName].forEach((wall: Wall) => {
                this.fillMonitors(wall);
            });
        } else {
            this.fillMonitors(preset[groupName]);
        }
    }

    private fillMonitors(wall: Wall): void {
        if (wall && wall.Monitors && wall.Monitors.length) {
            wall.Monitors.filter(x => !!x).forEach(
                (monitor: WallMonitor, i: number) => (monitor.DisplayText = monitor.DisplayText || `${i + 1}`)
            );

            wall.Monitors.map((monitor: WallMonitor) => {
                if (monitor) {
                    return new WallMonitor({ ...monitor, GroupId: wall.Id });
                }
            });
        }
    }
}
